import { ArrowUpDown, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// Import enums for type derivation, actual values will be passed or fetched if needed for filtering UI
import type { orderStatusEnum, paymentMethodEnum } from "@/server/db/schema/orders";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { CheckedState } from "@radix-ui/react-checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next"; // For t function type

// Define the OrderStatus and PaymentMethod types based on your schema enums
export type OrderStatus = (typeof orderStatusEnum.enumValues)[number];
export type PaymentMethod = (typeof paymentMethodEnum.enumValues)[number];

export type OrderRow = {
  id: string;
  orderNumber: string;
  customerEmail: string;
  user?: { email: string | null } | null;
  orderItems: {
    id: string; // Added id for keying if needed
    product?: { name: string | null } | null;
    quantity: number;
    activationCodes?: { code: string; status: string; expiresAt: string | Date }[];
  }[];
  totalAmount: string; // Comes as string from numeric type in DB
  currency: string;
  orderStatus: OrderStatus;
  paymentMethod: PaymentMethod;
  createdAt: string | Date; // Date object or ISO string
};

const getOrderStatusVariant = (status: OrderStatus) => {
  switch (status) {
    case "pending_payment":
      return "outline";
    case "paid":
    case "manually_marked_paid":
      return "secondary"; // Or "default" if you want it to be more prominent like "completed" was
    case "cancelled":
    case "refunded":
      return "destructive";
    default:
      return "secondary"; // Fallback for any unexpected status
  }
};

const getPaymentMethodVariant = (method: PaymentMethod) => {
  switch (method) {
    case "credit_card":
    case "stripe":
    case "paypal":
      return "default";
    case "bank_transfer":
    case "crypto":
      return "secondary";
    default:
      return "outline";
  }
};


export const getOrderColumns = (
  t: TFunction, // t function from useTranslation
  onViewDetails: (orderId: string) => void,
  onEditStatus: (order: OrderRow) => void,
  onCancelOrder: (orderId: string, orderNumber: string) => void, // Added for cancel
  onMarkAsPaid: (orderId: string, orderNumber: string) => void, // Added for Mark as Paid
): ColumnDef<OrderRow>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
            ? "indeterminate"
            : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t("admin.adminOrders.columns.selectAll")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t("admin.adminOrders.columns.selectRow")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "orderNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {t("admin.adminOrders.columns.orderNumber")}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div className="font-medium">{row.getValue("orderNumber")}</div>,
  },
  {
    accessorKey: "customerEmail",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {t("admin.adminOrders.columns.customerEmail")}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div>{row.original.user?.email || row.getValue("customerEmail")}</div>,
  },
  {
    accessorKey: "orderItems",
    header: t("admin.adminOrders.columns.products"),
    cell: ({ row }) => {
      const items = row.getValue("orderItems") as OrderRow["orderItems"];
      if (!items || items.length === 0) return "-";
      const displayItems = items.slice(0, 2);
      const remainingCount = items.length - displayItems.length;
      return (
        <div className="truncate max-w-xs">
          {displayItems.map(item => `${item.product?.name || t("admin.adminOrders.unknownProduct")} (x${item.quantity})`).join(", ")}
          {remainingCount > 0 && ` + ${remainingCount} ${t("admin.adminOrders.more")}`}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "totalAmount",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {t("admin.adminOrders.columns.totalAmount")}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const amount = Number.parseFloat(row.getValue("totalAmount"));
      const currency = row.original.currency;
      const formatted = new Intl.NumberFormat("en-US", { // Consider using user's locale
        style: "currency",
        currency: currency || "USD",
      }).format(amount);
      return <div className="text-right font-medium">{formatted}</div>;
    },
  },
  {
    accessorKey: "orderStatus",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {t("admin.adminOrders.columns.status")}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const status = row.getValue("orderStatus") as OrderStatus;
      return (
        <Badge variant={getOrderStatusVariant(status)}>
          {t(`enums.orderStatus.${status}`, status.replace(/_/g, " "))}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "paymentMethod",
    header: t("admin.adminOrders.columns.paymentMethod"),
    cell: ({ row }) => {
      const method = row.getValue("paymentMethod") as PaymentMethod;
      return (
        <Badge variant={getPaymentMethodVariant(method)}>
         {t(`enums.paymentMethod.${method}`, method.replace(/_/g, " "))}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {t("admin.adminOrders.columns.orderDate")}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const date = row.getValue("createdAt") as string | Date;
      return <div>{new Date(date).toLocaleDateString()}</div>; // Consider locale and time formatting
    },
  },
  {
   accessorKey: "activationCodes", // Virtual accessor, logic handled in cell
   header: t("admin.adminOrders.columns.activationCodes", "Activation Codes"), // Added translation key
   cell: ({ row }) => {
     const orderItems = row.original.orderItems;
     if (!orderItems || orderItems.length === 0) return "-";

     const allCodes: { code: string; productName?: string | null }[] = [];
     for (const item of orderItems) {
       if (item.activationCodes && item.activationCodes.length > 0) {
         for (const ac of item.activationCodes) {
           allCodes.push({ code: ac.code, productName: item.product?.name });
         }
       }
     }

     if (allCodes.length === 0) return <span className="text-xs text-muted-foreground">{t("admin.adminOrders.noCodesGenerated", "No codes yet")}</span>;

     return (
       <div className="max-w-xs truncate">
         {allCodes.map((ac, index) => (
           <Badge key={`${row.id}-ac-${index}`} variant="outline" className="mr-1 mb-1 text-xs">
             {ac.code}
           </Badge>
         ))}
       </div>
     );
   },
   enableSorting: false,
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const order = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{t("admin.adminOrders.actions.openMenu")}</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t("admin.adminOrders.actions.title")}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onViewDetails(order.id)}>
              {t("admin.adminOrders.actions.viewDetails")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEditStatus(order)}>
              {t("admin.adminOrders.actions.editStatus")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMarkAsPaid(order.id, order.orderNumber)}>
              {t("admin.adminOrders.actions.markAsPaid")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onCancelOrder(order.id, order.orderNumber)}
            >
              {t("admin.adminOrders.actions.cancelOrder")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
];